import { Button } from '@/ui'
import { cls } from '@/ui/cls'
import { fmtTime } from '@/shared/lib/format'
import { READ_ONLY_TITLE, useCanEdit } from '@/shared/write-access'

interface Props {
  onSwitch: () => void
  // Absent when the signed-in user lacks `log_calls`. Pulling is what that
  // permission governs, so the affordance has to disappear with it — otherwise
  // the only action on this screen 403s, and apiFetch renders that as "CRM
  // access not granted", which reads as though the whole app locked them out.
  onTake?: (count: number) => void
  taking?: boolean
  // How many leads are in the shared pool right now. undefined = still loading.
  // Showing the number matters: a button that might do nothing is worse than a
  // screen that says there is nothing to do.
  available?: number
  // Open leads promised later today (not due yet, so not in the queue right
  // now) — without this, finishing the queue reads as "nothing left to call"
  // even on a day with three more promised callbacks still ahead.
  callbacksDueToday?: number
  nextCallbackAt?: string | null
}

export default function DoneState({
  onSwitch,
  onTake,
  taking = false,
  available,
  callbacksDueToday = 0,
  nextCallbackAt = null,
}: Props) {
  const canEdit = useCanEdit()
  const poolEmpty = available === 0
  const canTake = !!onTake && !poolEmpty
  const hasCallbacksLater = callbacksDueToday > 0

  // Reaching this screen means the leads assigned to you are all worked, which
  // is not the same as there being no work: the pool may still hold plenty.
  // Hence the count below, and why taking more is the primary action.
  const headline = "Du har bearbetat alla dina leads."

  let sub: string
  if (poolEmpty) {
    sub = 'Alla leads inom ditt område är bearbetade eller tilldelade. Inget kvar att ringa just nu.'
  } else if (canTake) {
    sub = available === undefined
      ? 'Öppna listan för att se pipelinen.'
      : `${available} otilldelad${available === 1 ? '' : 'e'} lead${available === 1 ? '' : 's'} kvar — ta några för att fortsätta ringa.`
  } else {
    sub = 'Öppna listan för att se pipelinen.'
  }

  return (
    <div className="grid place-content-center text-center px-6 py-20 h-full">
      <div className="w-[72px] h-[72px] rounded-full bg-brand grid place-items-center text-[32px] text-ink-1000 mx-auto mb-5">
        ✓
      </div>
      <h2 className="font-display text-[32px] font-medium tracking-tighter mt-0 mb-1.5">
        {headline}
      </h2>
      <p className={cls('text-ink-500 mt-0', hasCallbacksLater ? 'mb-1.5' : 'mb-5')}>{sub}</p>
      {hasCallbacksLater && (
        <p className="text-ink-500 mt-0 mb-5">
          {callbacksDueToday} återuppringning{callbacksDueToday === 1 ? '' : 'ar'} kvar idag
          {nextCallbackAt && (
            <>, nästa kl. <strong className="text-ink-1000 font-semibold">{fmtTime(nextCallbackAt)}</strong></>
          )}.
        </p>
      )}
      <div className="flex gap-2.5 justify-center">
        {canTake && (
          <>
            <Button variant="primary" onClick={() => onTake!(1)} disabled={taking || !canEdit} title={canEdit ? undefined : READ_ONLY_TITLE}>
              {taking ? 'Hämtar…' : 'Ta nästa lead'}
            </Button>
            {(available === undefined || available >= 5) && (
              <Button onClick={() => onTake!(5)} disabled={taking || !canEdit} title={canEdit ? undefined : READ_ONLY_TITLE}>
                Ta 5
              </Button>
            )}
          </>
        )}
        <Button variant={canTake ? 'default' : 'primary'} onClick={onSwitch}>
          Öppna listan
        </Button>
      </div>
    </div>
  )
}
